import { Shield, Clock, Award, Users, FileText, Truck, ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import HowItWorks from '@/components/HowItWorks';
import WhyChooseUs from '@/components/WhyChooseUs';

export default function AboutPage() {
  return (
    <Layout>
      {/* Hero Section with Image */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-hero z-10" />
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2V8ZW58MHx8fHwxNzc4MTA4MjA2fDA&ixlib=rb-4.1.0&q=85)'
            }}
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[120px] rounded-full z-10" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-slide-up">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
              About Us
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Your Trusted <span className="text-gradient">Logistics Partner</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Over 15 years of excellence in courier and freight services across the Greater Toronto Area and Ontario. Building lasting relationships through reliability, professionalism, and exceptional service.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto animate-slide-up" style={{ animationDelay: '0.1s' }}>
            {[
              { value: '2009', label: 'Founded' },
              { value: '500+', label: 'Business Clients' },
              { value: '50K+', label: 'Annual Deliveries' },
              { value: '15+', label: 'Years Experience' }
            ].map((stat, index) => (
              <Card key={index} className="p-6 bg-card/80 backdrop-blur-sm border-border/50 text-center">
                <div className="text-3xl font-bold text-gradient mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Story with Image */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
            <div className="scroll-animate">
              <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
                Our Story
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Building <span className="text-gradient">Trust</span> Through Excellence
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p className="leading-relaxed">
                  Founded in 2009, Syncline Logistics began with a simple mission: to provide businesses in the Greater Toronto Area with reliable, professional courier and freight services they could count on.
                </p>
                <p className="leading-relaxed">
                  Over the past 15+ years, we've grown from a small local courier service to one of the most trusted logistics partners in Ontario. Our success is built on the foundation of reliability, professionalism, and a genuine commitment to our clients' success.
                </p>
                <p className="leading-relaxed">
                  Today, we serve over 500 businesses across GTA and Ontario, completing more than 50,000 deliveries annually with a 99.5% on-time delivery rate. But we're not just about numbers—we're about relationships, trust, and going the extra mile for every single client.
                </p>
              </div>
            </div>
            <div className="scroll-animate" style={{ animationDelay: '0.15s' }}>
              <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
                <img 
                  src="https://images.unsplash.com/photo-1565891741441-64926e441838?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc4MTA0MDAxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Syncline Logistics Fleet"
                  className="w-full h-auto"
                />
              </Card>
            </div>
          </div>

          {/* Team Image Section */}
          <div className="scroll-animate">
            <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4">
                    Meet Our <span className="text-gradient">Professional Team</span>
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Behind every successful delivery is a dedicated team of logistics professionals. From our customer service representatives to our experienced drivers, every member of the Syncline family is committed to providing exceptional service.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-sm">50+ Team Members</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Award className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-sm">Certified & Trained Professionals</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Clock className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-sm">24/7 Customer Support</span>
                    </div>
                  </div>
                </div>
                <img 
                  src="https://images.pexels.com/photos/6868948/pexels-photo-6868948.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Syncline Logistics Team"
                  className="w-full h-full object-cover"
                />
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Component */}
      <WhyChooseUs />

      {/* Mission & Values */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Our Mission <span className="text-gradient">& Values</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do and define who we are as a company
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: 'Reliability',
                description: 'We understand that your shipments represent your business reputation. Every delivery is handled with the utmost care and professionalism, ensuring your cargo arrives safely and on time.'
              },
              {
                icon: Users,
                title: 'Customer First',
                description: 'Your success is our success. We go above and beyond to ensure your logistics needs are met with personalized service, responsive support, and flexible solutions.'
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'We continuously invest in our fleet, technology, and team training to provide the best possible service. Our commitment to excellence drives everything we do.'
              },
              {
                icon: Clock,
                title: 'Timeliness',
                description: 'Time-sensitive deliveries are our specialty. We maintain a 99.5% on-time delivery rate through meticulous planning, efficient routing, and proactive communication.'
              },
              {
                icon: Truck,
                title: 'Safety',
                description: 'Safety is paramount in everything we do. All our vehicles are regularly maintained, and our drivers are fully trained, licensed, and certified professionals.'
              },
              {
                icon: FileText,
                title: 'Transparency',
                description: 'No hidden fees, no surprises. We provide clear, upfront pricing and real-time tracking for complete visibility throughout your shipment journey.'
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <Card 
                  key={index}
                  className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-glow group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-3 rounded-xl bg-gradient-primary inline-flex mb-4 group-hover:shadow-glow transition-all duration-300">
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Component */}
      <HowItWorks />

      {/* Facilities Image */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="scroll-animate">
            <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
              <img 
                src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxwYWNrYWdlc3xlbnwwfHx8fDE3NzgxMDgyMTN8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern Warehouse Facility"
                className="w-full h-96 object-cover"
              />
              <div className="p-8 md:p-12">
                <h3 className="text-2xl font-semibold mb-4">State-of-the-Art Facilities</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Our modern warehouse and distribution facilities are equipped with advanced security systems, climate control capabilities, and efficient loading docks. We handle every package with care, from small envelopes to large freight shipments.
                </p>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <div className="text-2xl font-bold text-gradient mb-1">10K+</div>
                    <div className="text-sm text-muted-foreground">Sq Ft Warehouse Space</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gradient mb-1">24/7</div>
                    <div className="text-sm text-muted-foreground">Security Monitoring</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gradient mb-1">100%</div>
                    <div className="text-sm text-muted-foreground">Climate Controlled</div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <Card className="scroll-animate p-12 bg-gradient-card backdrop-blur-sm border-primary/20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Experience the <span className="text-gradient">Syncline Difference?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join hundreds of satisfied businesses who trust Syncline Logistics with their delivery needs. Experience reliability, professionalism, and service excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button size="lg" className="bg-gradient-primary hover:shadow-glow-lg transition-all duration-300 text-lg px-8 w-full sm:w-auto">
                  Get a Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:(647)-642-0062">
                <Button size="lg" variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 text-lg px-8 w-full sm:w-auto">
                  Call: (647)-642-0062
                </Button>
              </a>
            </div>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
